import Vue from "vue";
import Vuex from "vuex";
import { set, findIndex, some, find } from "lodash";
import { DateTime } from "luxon";

import AccountServices from "@/services/account";
import client from "./client";
import task from "./task";
import patient from "./patient";
import conversation from "./conversation";
import references from "./reference";
import inventory from "./inventory";
import report from "./report";
import contact from "./contact";
import settingsTemplate from "./settings-template";
import inventoryOrder from "./inventory-order";
import inventoryReceipt from "./inventory-receipt";
import billing from "./billing";
import webform from "./webform";
import soap from "./soap";
import returnDetails from "./return";
import modalReservation from "./modal-reservation";
import EstimateService from "@/services/billing/billing-estimate.js";
import userPreferences from "./user-preferences";
import notification from "./notification";
import staff from "./staff";
import address from "./address";
import censusPreferences from "./census-preferences";

import BETA_RELEASE from "@/constants/beta-release";

Vue.use(Vuex);
/*
- mutation is the only way to modify state
- mutation doesn't care about business logic, it just cares about "state"
- action is business logic
- action can dispatch more than 1 mutation at a time, it
  just implements the business logic, it doesn't care about
  data changing (which manage by mutation)
*/
export default new Vuex.Store({
    state: {
        patients: {},
        setting: {},
        reference: {
            "master-vital": [],
            "master-vaccination-route": [],
            "master-vaccination-location": [],
            "treatment-task-label": []
        },
        status: [],
        locked_activity: {
            locked: null, // current instance lock
            list: [] // other instances lock
        },
        currency: null,
        billingLockModal: {
            isOpen: false,
            proceedCallback: null,
            administratorList: [],
            selectedAdministrator: null
        },
        useVueEstimate: false,
        featuresEnabled: {
            [BETA_RELEASE.KEY.NOTIFICATION]: false
        },
        isBusinessPage: false,
        isBusinessLoading: false,
        isBusinessNotFound: false
    },
    getters: {
        locked: state => activity_id => {
            if (
                some(state.locked_activity.list) &&
                some(state.locked_activity.locked)
            ) {
                let lock = find(state.locked_activity.list, {
                    id: activity_id
                });

                if (
                    lock &&
                    DateTime.fromISO(lock.time) <
                        state.locked_activity.locked.time
                ) {
                    return lock;
                }
            }
            return null;
        },
        setting: state => {
            return state.setting;
        },
        currency: state => {
            return state.currency;
        },
        reference: state => {
            return state.reference;
        },
        useVueEstimate: state => {
            return state.useVueEstimate;
        },
        featuresEnabled: state => {
            return state.featuresEnabled;
        },
        isBusinessPage: state => {
            return state.isBusinessPage;
        },
        isBusinessLoading: state => {
            return state.isBusinessLoading;
        },
        isBusinessNotFound: state => {
            return state.isBusinessNotFound;
        }
    },
    mutations: {
        patients(store, payload) {
            set(store.patients, payload.id + "." + payload.path, payload.value);
        },
        setting(store, payload) {
            if (payload.path) {
                set(store.setting, payload.path, payload.value);
            } else {
                store.setting = payload.value;
            }
        },
        reference(store, payload) {
            set(
                store.reference,
                payload.path ? payload.path : "",
                payload.value
            );
        },
        status(store, payload) {
            store.status = payload;
        },
        locked_activity(store, payload) {
            store.locked_activity.locked = payload;
        },
        locked_activity_add(store, payload) {
            let index = findIndex(store.locked_activity.list, {
                id: payload.id
            });
            if (index < 0) {
                store.locked_activity.list.push(payload);
            } else {
                Vue.set(store.locked_activity.list, index, payload);
            }
        },
        locked_activity_remove(store, payload) {
            let index = findIndex(store.locked_activity.list, {
                id: payload.id
            });

            if (index > -1) {
                store.locked_activity.list.splice(index, 1);
            }
        },
        setCurrency(store, payload) {
            store.currency = payload;
        },
        showBillingLockModal(store, payload) {
            store.billingLockModal.isOpen = true;
            store.billingLockModal.proceedCallback = payload.callback;
            store.billingLockModal.administratorList =
                payload.administratorList;

            if (
                !store.billingLockModal.selectedAdministrator &&
                payload.administratorList[0]
            ) {
                store.billingLockModal.selectedAdministrator =
                    payload.administratorList[0].id;
            }
        },
        hideBillingLockModal(store) {
            store.billingLockModal.isOpen = false;
        },
        updateBillingLockModal(store, payload) {
            store.billingLockModal = {
                ...store.billingLockModal,
                ...payload
            };
        },
        setViewEstimateEnableStatus(store, status) {
            store.useVueEstimate = status;
        },
        setFeaturesEnabled(store, payload) {
            const { key, isEnabled } = payload;

            if (key && store.featuresEnabled) {
                store.featuresEnabled[key] = isEnabled;
            }
        },
        setIsBusinessPage(store, payload) {
            store.isBusinessPage = payload;
        },
        setIsBusinessLoading(store, payload) {
            store.isBusinessLoading = payload;
        },
        setIsBusinessNotFound(store, payload) {
            store.isBusinessNotFound = payload;
        }
    },
    actions: {
        async loadReference({ commit }) {
            this._vm.$http
                .get(process.env.BARRAMUNDI + "reference/master-vital")
                .then(response => {
                    commit("reference", {
                        path: "master-vital",
                        value: response.data.response.resources
                    });
                });
            this._vm.$http
                .get(
                    process.env.BARRAMUNDI +
                        "reference/master-vaccination-route"
                )
                .then(response => {
                    commit("reference", {
                        path: "master-vaccination-route",
                        value: response.data.response.resources
                    });
                });
            this._vm.$http
                .get(
                    process.env.BARRAMUNDI +
                        "reference/master-vaccination-location"
                )
                .then(response => {
                    commit("reference", {
                        path: "master-vaccination-location",
                        value: response.data.response.resources
                    });
                });
            this._vm.$http
                .get(process.env.BARRAMUNDI + "reference/treatment-task-label")
                .then(response => {
                    let labels = response.data.response.resources.map(
                        label => ({ value: label.id, text: label.name })
                    );
                    commit("reference", {
                        path: "treatment-task-label",
                        value: labels
                    });
                });
        },
        async checkBillingLock({ commit }, payload) {
            const { date, onProceed, onError } = payload;

            const res = await AccountServices.checkBillingLock({ date: date });
            const { message, resource } = res;

            if (message && message.code === 200) {
                if (resource.isBillingEditable) {
                    onProceed(); // proceed without billing lock
                } else {
                    commit("showBillingLockModal", {
                        callback: payload.onProceed,
                        administratorList: resource.administrators
                    });
                }
            } else {
                onError("Check billing lock error. Unable to proceed.");
            }
        },
        proceedBillingLock({ state, commit }) {
            commit("hideBillingLockModal");

            if (state.billingLockModal.proceedCallback) {
                state.billingLockModal.proceedCallback();
            }
        },
        async getNewEstimateEnableStatus({ commit }) {
            const useViewEstimate = await EstimateService.isNewEstimateEnabled();
            commit("setViewEstimateEnableStatus", useViewEstimate);
        },
        async checkFeatureEnabled({ commit }, payload) {
            const { key } = payload;
            const res = await AccountServices.checkFeatureEnabled(key);
            const { message, resource } = res;

            if (message && message.code === 200) {
                commit("setFeaturesEnabled", { key, isEnabled: resource });
            }
        }
    },
    modules: {
        client,
        task,
        patient,
        conversation,
        references,
        inventory,
        contact,
        settingsTemplate,
        report,
        inventoryOrder,
        inventoryReceipt,
        returnDetails,
        modalReservation,
        billing,
        webform,
        soap,
        userPreferences,
        notification,
        staff,
        address,
        censusPreferences
    }
});
